import { useAtom } from "jotai";
import { FC, useLayoutEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";

import { ChevronLeft } from "@/assets/icons/ds";
import { WelcomeBenefits } from "@/components/FriendsWithBenefits/Sections/WelcomeBenefits";
import { WelcomeDiscountTeaser } from "@/components/FriendsWithBenefits/Sections/WelcomeDiscountTeaser";
import { WelcomeRateCards } from "@/components/FriendsWithBenefits/Sections/WelcomeRateCards";
import { SlideShow } from "@/components/FriendsWithBenefits/SlideShow";
import { PortalTopBanners } from "@/components/Interface/Alert/PortalTopBanners";
import { Button } from "@/components/Interface/Button/Button";
import { Link } from "@/components/Interface/Link/Link";
import { DefaultPageLayout } from "@/components/Layout/BaseLayouts/DefaultPageLayout";
import { PageSection } from "@/components/Layout/BaseLayouts/PageSection";
import { useHandleJoinTribe } from "@/hooks/useHandleJoinTribe";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetSimpleTribe } from "@/services/api";
import { accessTokenAtom, isAnonymousUserAtom } from "@/utils/atoms";
import { LogInForm } from "@/views/WelcomeScreen/LogInForm";
import { RegisterForm } from "@/views/WelcomeScreen/RegisterForm";

const LearnMoreSection: FC = () => {
  const { t } = useTranslation();

  return (
    <p className="py-4 text-center text-body-xs leading-md text-foreground-primary desktop:py-3">
      <Trans
        i18nKey="friendsWithBenefits.welcome.notConvinced"
        components={[
          // Own line on mobile, same line as the question from desktop up.
          <a
            key="0"
            className="mx-auto block w-fit border-b border-solid border-foreground-primary font-semibold text-foreground-primary hover:-mb-px hover:border-b-2 focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-4 focus-visible:!outline-foreground-primary desktop:mx-0 desktop:inline"
            href={t("friendsWithBenefits.welcome.link")}
            target="_blank"
            rel="noreferrer"
          />,
        ]}
      />
    </p>
  );
};

export const WelcomeFriendsWithBenefits: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accessToken] = useAtom(accessTokenAtom);
  const [isAnonymousUser] = useAtom(isAnonymousUserAtom);
  const joinTribe = useHandleJoinTribe();

  const [isCTA, setIsCTA] = useState(false);
  const [switchForm, setSwitchForm] = useState(true);
  const [showSlideShow, setShowSlideShow] = useState(false);

  const { tribeId, tribe2CustomerId } = useParams<{
    tribeId: string;
    tribe2CustomerId: string;
  }>();

  const { data: tribe } = useGetSimpleTribe(
    tribeId ?? "",
    tribe2CustomerId ?? "",
    {
      query: {
        enabled: true,
      },
    },
  );

  const referralName =
    tribe?.fwbFirstName || t("friendsWithBenefits.welcome.unknown");
  const tribeDiscount = tribe?.tribeDiscountNext ?? 0;

  const handleJoinTribe = (tribeIdToJoin: string) => {
    joinTribe(tribeIdToJoin, () => {
      navigate(portalRoutes.friendsWithBenefits.groupOverview);
    });
  };

  const isValid =
    /^\d{8}$/.test(tribeId || "") && /^\d{9,10}$/.test(tribe2CustomerId || "");
  const shouldShowAuthCta = !accessToken || isAnonymousUser;

  // When Join now swaps the long welcome page for the signup form, bring the
  // form into view — otherwise the previous scroll position can leave it off-screen.
  useLayoutEffect(() => {
    if (!isCTA) {
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [isCTA]);

  if (!isValid) {
    return <Navigate to="/404" replace />;
  }

  const handleCTAClick = () => {
    if (shouldShowAuthCta) {
      setIsCTA(!isCTA);

      return;
    }

    if (tribeId) {
      handleJoinTribe(tribeId);
    }
  };

  return (
    <DefaultPageLayout
      headline={t("Common.products.friendsWithBenefits")}
      headlineIsPageTitle
      heroTheme="hands"
    >
      <PortalTopBanners className="z-40 col-span-full" placement="welcome" />

      {isCTA ? (
        <div
          data-testid="fwb-welcome-content"
          className="col-span-full flex flex-col items-center gap-6 py-section-padding-vertical"
        >
          <div className="flex w-full max-w-[599px] flex-col gap-6">
            <Button
              dataTestid="fwb-welcome-back"
              variant="tertiary"
              ariaLabel={t("Common.label.back")}
              icon={<ChevronLeft />}
              onClick={() => setIsCTA(false)}
            />

            {switchForm ? (
              <RegisterForm
                // The hero headline is this page's h1.
                headingLevel={2}
                afterRegister={async () => {
                  if (!tribeId) {
                    return;
                  }

                  try {
                    await joinTribe(tribeId);
                  } catch {
                    // Error toast is shown by useHandleJoinTribe; continue to onboarding.
                  }
                }}
              >
                <p className="text-body-xs text-foreground-primary">
                  <span className="mr-1">
                    {t("newWelcome.registerForm.accountExists")}
                  </span>
                  <button
                    className="inline-block w-fit cursor-pointer border-b border-solid border-foreground-primary pb-px text-body-xs !font-semibold text-foreground-primary hover:border-b-2 hover:pb-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground-primary"
                    type="button"
                    onClick={() => setSwitchForm(!switchForm)}
                  >
                    {t("newWelcome.registerForm.logInNow")}
                  </button>
                </p>
              </RegisterForm>
            ) : (
              <LogInForm
                headingLevel={2}
                joinTribeOnLogin={() => {
                  if (tribeId) {
                    handleJoinTribe(tribeId);
                  }
                }}
              >
                <p className="text-body-xs text-foreground-primary">
                  <span className="mr-1">
                    {t("newWelcome.logInForm.noAccount")}
                  </span>
                  <button
                    className="inline-block w-fit cursor-pointer border-b border-solid border-foreground-primary pb-px text-body-xs !font-semibold text-foreground-primary hover:border-b-2 hover:pb-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground-primary"
                    type="button"
                    onClick={() => setSwitchForm(!switchForm)}
                  >
                    {t("newWelcome.logInForm.registerNow")}
                  </button>
                </p>
              </LogInForm>
            )}

            <LearnMoreSection />
          </div>
        </div>
      ) : (
        <div
          data-testid="fwb-welcome-content"
          className="col-span-full flex flex-col"
        >
          <PageSection
            className="flex flex-col items-center gap-8"
            spacing="vertical"
          >
            <div className="flex max-w-[475px] flex-col gap-gutter text-center text-foreground-primary">
              <h2 className="text-headline-md font-semibold leading-sm">
                {t("friendsWithBenefits.welcome.greeting")}
              </h2>
              <p className="text-body-base leading-none">
                <Trans
                  i18nKey="friendsWithBenefits.welcome.youWereInvited"
                  components={[<span key="key" className="font-semibold" />]}
                  values={{ user: referralName }}
                />
                {/* Empty space infront of text is intentional */}
                {` ${t("friendsWithBenefits.welcome.referralCTA")}`}
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <Button variant="primary" onClick={handleCTAClick}>
                {t("friendsWithBenefits.welcome.joinNow")}
              </Button>

              <Link type="button" onClick={() => setShowSlideShow(true)}>
                {t("friendsWithBenefits.welcome.stories")}
              </Link>
            </div>
          </PageSection>

          <PageSection
            className="flex flex-col gap-section-gap"
            background="primary"
            width="full"
          >
            <PageSection className="desktop:p-8" spacing="none">
              <WelcomeDiscountTeaser tribeDiscount={tribeDiscount} />
            </PageSection>

            <PageSection
              className="flex flex-col items-center gap-section-gap desktop:px-4"
              spacing="none"
            >
              <WelcomeRateCards tribeDiscount={tribeDiscount} />

              <div className="flex flex-col items-center gap-4">
                <Button variant="primary" onClick={handleCTAClick}>
                  {t("friendsWithBenefits.welcome.joinNow")}
                </Button>

                <Link type="button" onClick={() => setShowSlideShow(true)}>
                  {t("friendsWithBenefits.welcome.stories")}
                </Link>
              </div>
            </PageSection>
          </PageSection>

          <PageSection
            className="pb-section-padding-vertical desktop:p-8"
            spacing="none"
          >
            <WelcomeBenefits />
          </PageSection>

          <LearnMoreSection />
        </div>
      )}

      {showSlideShow && (
        <SlideShow
          onClose={() => setShowSlideShow(false)}
          onCTAClick={() => {
            handleCTAClick();
            setShowSlideShow(false);
          }}
        />
      )}
    </DefaultPageLayout>
  );
};
